// src/components/vault/documents/id-validation/types.ts

/**
 * Steps in the ID validation flow state machine
 */
export type IDValidationStep =
  | "upload" // Initial state - user uploads document
  | "validating" // Loader showing validation progress
  | "pending_review" // Document sent for human review
  | "rejected" // Document rejected (backend returned status "invalid")
  | "address_check" // "Is your address updated?" (INE only, status "valid" or "pending_review")
  | "completed"; // Flow finished, unlock other steps

/**
 * Address extracted from INE
 */
export interface ExtractedAddress {
  calle: string;
  numeroExterior: string;
  numeroInterior: string;
  colonia: string;
  codigoPostal: string;
  municipio: string;
  estado: string;
}

export type NipDestinationDialCode = "+52" | "+1";

/**
 * Data extracted from INE document
 */
export interface ExtractedINEData {
  nombre: string;
  segundoNombre: string;
  apellidoPaterno: string;
  apellidoMaterno: string;
  direccion: ExtractedAddress;
  curp: string;
  rfc: string;
  claveElector: string;
  fechaNacimiento: string;
  seccionElectoral: string;
  vigencia: string;
  genero: string;
  email: string;
  phone_number: string;
  dial_code: NipDestinationDialCode;
}

/**
 * State of the ID validation flow
 */
export interface IDValidationState {
  currentStep: IDValidationStep;
  documentType: string | null;
  extractedData: ExtractedINEData | null;
  isAddressUpdated: boolean | null;
  hasProofOfAddress: boolean; // Whether the current subfolder has a POA requirement
  validationProgress: number; // 0-100
  error: string | null; // Error message from verification API
  invalidReason: string | null; // Reason(s) from backend when status is "invalid" (\n-separated)
}

/**
 * Actions for the ID validation flow
 */
export type IDValidationAction =
  | {
      type: "START_VALIDATION";
      documentType: string;
      hasProofOfAddress: boolean;
    }
  | { type: "UPDATE_PROGRESS"; progress: number }
  | { type: "VALIDATION_COMPLETE"; extractedData: ExtractedINEData | null }
  | { type: "REQUIRE_PROOF_OF_ADDRESS"; extractedData: ExtractedINEData | null }
  | { type: "SET_ADDRESS_UPDATED"; isUpdated: boolean }
  | { type: "COMPLETE_FLOW" }
  | { type: "RESET_FLOW" }
  | { type: "VALIDATION_ERROR"; error: string }
  | { type: "SET_PENDING_REVIEW" } // Backend returned status "pending_review"
  | { type: "SET_REJECTED"; invalidReason: string | null } // Backend returned status "invalid"
  | { type: "SET_COMPLETED" } // For page reload state detection
  | { type: "RESTORE_ADDRESS_CHECK"; extractedData: ExtractedINEData | null } // Restore address_check step from backend on page reload
  | { type: "SET_HAS_PROOF_OF_ADDRESS"; hasProofOfAddress: boolean }; // Set whether subfolder has POA requirement

/**
 * Document types that require both sides
 */
export const DOCUMENTS_WITH_BOTH_SIDES = [
  "ine",
  "driver_license",
  "migratory_form",
];

/**
 * Document types that show the address confirmation flow (INE only)
 */
export const DOCUMENTS_WITH_ADDRESS_FLOW = ["ine"];

/**
 * Props for the ID validation loader component
 */
export interface IDValidationLoaderProps {
  progress: number;
  documentType: string;
}

/**
 * Props for the address confirmation form
 */
export interface AddressConfirmationFormProps {
  address: ExtractedAddress;
  onConfirm: (isUpdated: boolean) => void | Promise<void>;
  isSubmitting?: boolean;
}

/**
 * Props for the ID data confirmation form
 */
export interface IDDataConfirmationFormProps {
  data: ExtractedINEData;
  onConfirm: () => void | Promise<void>;
  onEdit: () => void;
  isSubmitting?: boolean;
  requiresBdcAuthorization?: boolean;
}

/**
 * Props for the ID data edit form
 */
export interface IDDataEditFormProps {
  contactReadOnly?: boolean;
  data: ExtractedINEData;
  onSave: (data: ExtractedINEData) => void | Promise<void>;
  onCancel: () => void;
  /** When false, hides the Cancel button (used when edit is forced due to missing required fields). */
  allowCancel?: boolean;
  isSubmitting?: boolean;
  /** Apply BDC-only contact and provider-format requirements. */
  requiresBdcAuthorization?: boolean;
  /** Apply the field rules while BDC inputs can still be corrected. */
  strictBdcValidation?: boolean;
  /** Backend validation errors to show inline after a rejected confirmation. */
  serverFieldErrors?: Record<string, string>;
  /** Required only for a natural person's mutable BDC inputs. */
  requireBirthDate?: boolean;
}
